import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchOrganizationDetails, fetchOrganizationPlan, fetchAvailableFeatures, updateOrganizationFeatures } from '@/data/api/cas';
import { Button } from '@/lib/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/lib/components/ui/card';
import { Switch } from '@/lib/components/ui/switch';
import useNotify from '@/hooks/useNotify';
import { ArrowLeft, Building2, Zap, Settings, Shield, Activity } from 'lucide-react';
import { motion } from 'motion/react';

export const OrganizationDetails: React.FC = () => {
  const notify = useNotify();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [availablePerks, setAvailablePerks] = useState<string[]>([]);
  const [savingPerks, setSavingPerks] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!id) return;
    loadData(id);
  }, [id]);

  const loadData = async (orgId: string) => {
    try {
      setLoading(true);
      const [orgData, planData, featuresData] = await Promise.all([
        fetchOrganizationDetails(orgId),
        fetchOrganizationPlan(orgId),
        fetchAvailableFeatures()
      ]);
      
      setOrg(orgData);
      setPlan(planData.plan || null);
      
      const perks = (featuresData.perks || []).filter((p: string) => p !== 'audit');
      setAvailablePerks(perks);
    } catch (err) {
      console.error('Failed to load organization details:', err);
      notify.error('Failed to load organization data.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePerk = async (perk: string, currentValue: boolean) => {
    if (!id || !plan) return;
    
    const newValue = !currentValue;
    setSavingPerks(prev => ({ ...prev, [perk]: true }));
    
    try {
      const updatedPlan = await updateOrganizationFeatures(id, { [perk]: newValue });
      setPlan(updatedPlan);
      notify.success(`Feature '${perk}' ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      notify.error(`Failed to update feature '${perk}'`);
    } finally {
      setSavingPerks(prev => ({ ...prev, [perk]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-gray-500">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="font-medium animate-pulse">Loading organization...</p>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex flex-col min-h-[60vh] items-center justify-center text-center">
        <h2 className="text-2xl font-bold mb-3 text-gray-900">Organization not found</h2>
        <p className="text-gray-500 mb-6">The requested organization ID does not exist or you lack permission.</p>
        <Button onClick={() => navigate('/admin/organizations')} className="bg-blue-600 hover:bg-blue-700">Go Back</Button>
      </div>
    );
  }

  const activePerks = plan?.perks || {};

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8 max-w-5xl"
    >
      <div className="flex flex-col gap-6">
        <button 
          className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors w-fit" 
          onClick={() => navigate('/admin/organizations')}
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Organizations
        </button>

        <div className="relative overflow-hidden rounded-3xl border border-gray-200/60 bg-white/60 backdrop-blur-xl shadow-xl shadow-gray-200/40 p-8">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/40 to-purple-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          
          <div className="flex items-start gap-6 relative z-10">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100/50 flex items-center justify-center shrink-0 overflow-hidden shadow-inner">
              {org.image_url ? (
                <img src={org.image_url} alt={org.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-10 h-10 text-blue-500/50" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{org.name}</h1>
                <span className="px-2.5 py-1 rounded-full bg-blue-100/50 text-blue-700 text-xs font-semibold border border-blue-200/50">
                  {org.slug}
                </span>
              </div>
              <p className="text-gray-500 font-medium">
                Internal ID: <span className="font-mono text-xs ml-1 px-2 py-0.5 bg-gray-100 rounded text-gray-600">{org.id}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border-gray-200/60 shadow-lg shadow-gray-200/30 bg-white/70 backdrop-blur-md rounded-3xl overflow-hidden">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100 pb-6">
              <CardTitle className="flex items-center gap-2.5 text-xl">
                <div className="p-2 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg">
                  <Zap className="w-5 h-5 text-orange-500" />
                </div>
                Feature Management
              </CardTitle>
              <CardDescription className="text-sm mt-2 text-gray-500 font-medium">
                Toggle specific perks and capabilities for this organization. Changes are instantly applied to their plan.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {availablePerks.length === 0 ? (
                  <div className="text-sm text-gray-500 font-medium p-6 bg-gray-50 rounded-2xl border border-gray-100 text-center">
                    No configurable features available in the system.
                  </div>
                ) : (
                  availablePerks.map((perk, i) => {
                    const isEnabled = !!activePerks[perk];
                    const isSaving = savingPerks[perk];
                    
                    return (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        key={perk} 
                        onClick={() => !isSaving && handleTogglePerk(perk, isEnabled)}
                        className={`flex items-center justify-between p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${isEnabled ? 'bg-gradient-to-r from-blue-50/50 to-transparent border-blue-100/50 shadow-sm' : 'bg-gray-50/30 border-gray-100 hover:border-gray-200 hover:bg-gray-50/80'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2.5 rounded-xl ${isEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                            {isEnabled ? <Activity className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 tracking-tight capitalize">{perk.replace(/_/g, ' ')}</p>
                            <p className={`text-sm font-medium mt-0.5 ${isEnabled ? 'text-blue-600' : 'text-gray-500'}`}>
                              {isEnabled ? 'Active and accessible' : 'Disabled for this organization'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {isSaving && <span className="text-xs font-semibold text-blue-500 uppercase tracking-wider animate-pulse">Saving</span>}
                          <Switch
                            checked={isEnabled}
                            disabled={isSaving}
                            onCheckedChange={() => handleTogglePerk(perk, isEnabled)}
                            onClick={(e) => e.stopPropagation()}
                            className="data-[state=checked]:bg-blue-600"
                          />
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="lg:col-span-1">
          <Card className="border-gray-200/60 shadow-lg shadow-gray-200/30 bg-white/70 backdrop-blur-md rounded-3xl sticky top-8">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-500" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5 text-sm">
              <div>
                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Contact Email</span>
                <span className="font-medium text-gray-900">{org.contact_email || 'Not specified'}</span>
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Creation Date</span>
                <span className="font-medium text-gray-900">{new Date(org.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              {org.billing_vat && (
                <div>
                  <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">VAT Number</span>
                  <span className="font-medium text-gray-900 px-2 py-1 bg-gray-100 rounded inline-block">{org.billing_vat}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>
  );
};
